import { useApolloClient } from '@apollo/client'
import { useFormikContext } from 'formik'
import { DateTime } from 'luxon'
import { useCallback, useRef } from 'react'
import { flushSync } from 'react-dom'
import { useToasts } from 'react-toast-notifications'
import { PatientHistoryType } from 'src/graphql.generated'
import { DUMMY_GAD7, DUMMY_PHQ9 } from 'src/participantHistory/autofill/dummyScores'
import useAddPatientHistoryEntry from 'src/participantHistory/useAddPatientHistoryEntry'
import { getDummyIntakeNoteComprehensive } from './dummyIntakeNoteComprehensive'
import { IntakeNoteComprehensiveFormType } from './IntakeNoteComprehensiveFormSchema'
import {
  GetIntakeNoteComprehensivePatientDataDocument,
  GetIntakeNoteComprehensivePatientDataQuery,
  GetIntakeNoteComprehensivePatientDataQueryVariables,
} from './initialValues/getIntakeNoteComprehensivePatientData.generated'
import { mapHealthQuestionnaireToForm } from './initialValues/healthQuestionnaireTransform'

export default function useAutofillIntakeNoteComprehensive(uid: string) {
  const client = useApolloClient()
  const addPatientHistoryEntry = useAddPatientHistoryEntry()
  const { addToast } = useToasts()
  const { setValues } = useFormikContext<IntakeNoteComprehensiveFormType>()
  const inProgressAutofill = useRef<Promise<boolean> | null>(null)

  const autofill = useCallback(async () => {
    // Bypasses the cache on purpose: updating the form's own query changes its initialValues,
    // and enableReinitialize would then reset the values autofilled here.
    const fetchPatientData = async () => {
      const { data } = await client.query<
        GetIntakeNoteComprehensivePatientDataQuery,
        GetIntakeNoteComprehensivePatientDataQueryVariables
      >({
        query: GetIntakeNoteComprehensivePatientDataDocument,
        variables: { uid },
        fetchPolicy: 'no-cache',
      })
      return data.intakeNoteComprehensivePatientData
    }

    try {
      let patientData = await fetchPatientData()

      const date = DateTime.now().toFormat('yyyy-MM-dd')
      const missingEntries = [
        ...(patientData?.healthQuestionnaire?.phq9 ? [] : [{ type: PatientHistoryType.Phq9, value: DUMMY_PHQ9 }]),
        ...(patientData?.healthQuestionnaire?.gad7 ? [] : [{ type: PatientHistoryType.Gad7, value: DUMMY_GAD7 }]),
      ]
      if (missingEntries.length > 0) {
        await Promise.all(missingEntries.map(({ type, value }) => addPatientHistoryEntry({ uid, date, type, value })))
        patientData = await fetchPatientData()
      }

      const dummyValues = getDummyIntakeNoteComprehensive()
      // submitForm validates the values of the last render, so commit these before a double click submits.
      flushSync(() => {
        setValues({
          ...dummyValues,
          FIRST_NAME: patientData?.overallInformation?.firstName || dummyValues.FIRST_NAME,
          LAST_NAME: patientData?.overallInformation?.lastName || dummyValues.LAST_NAME,
          REASON_FOR_SEEKING_TREATMENT: {
            ...dummyValues.REASON_FOR_SEEKING_TREATMENT,
            HEALTH_QUESTIONNAIRE: mapHealthQuestionnaireToForm(
              patientData?.healthQuestionnaire
            ) as IntakeNoteComprehensiveFormType['REASON_FOR_SEEKING_TREATMENT']['HEALTH_QUESTIONNAIRE'],
          },
        })
      })
      return true
    } catch (e) {
      addToast('Failed to autofill the intake note and create the PHQ-9 and GAD-7 entries', {
        appearance: 'error',
      })
      return false
    }
  }, [addPatientHistoryEntry, addToast, client, setValues, uid])

  // A double click also fires two clicks, which must not create the entries more than once.
  return useCallback(() => {
    if (!inProgressAutofill.current) {
      inProgressAutofill.current = autofill().finally(() => {
        inProgressAutofill.current = null
      })
    }
    return inProgressAutofill.current
  }, [autofill])
}
